import {
  DomainAddressType,
  DomainBorrowerEmploymentStatus,
  DomainExpenseType,
  DomainFrequencyType,
  DomainIncomeType,
} from "@porchpass/api-client";
import type { ZodTypeAny } from "zod";
import type {
  ApplicationFormData,
  ApplicationStep,
  BorrowerData,
  EmploymentIncome,
} from "../types/applicationTypes";
import {
  additionalIncomeSubmitSchema,
  addressSubmitSchema,
  employmentSubmitSchema,
  expenseSubmitSchema,
  financialAccountSubmitSchema,
  homeSubmitSchema,
  mailingAddressSubmitSchema,
  maritalInfoSubmitSchema,
  personalInfoSubmitSchema,
  piiSubmitSchema,
  previousAddressSubmitSchema,
  pricingSubmitSchema,
  validateAddressFields,
  validateEmploymentForSubmit,
  validateLandForSubmit,
} from "../utils/validationSchemas";

/**
 * Pure validation orchestration for the DGA application. Lives here (not in the
 * modal) so it can be unit-tested against a plain ApplicationFormData. The Zod
 * rule definitions remain in utils/validationSchemas.ts; this module composes
 * them into the full required-fields check.
 */

/**
 * Run the full required-fields validation over the in-memory application data
 * and return a map of fieldPath -> error message (empty when everything passes).
 */
export function validateApplication(
  data: ApplicationFormData,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const collectErrors = (
    schema: ZodTypeAny,
    value: unknown,
    prefix: string,
  ) => {
    const result = schema.safeParse(value);
    if (!result.success) {
      for (const issue of result.error.issues) {
        const fieldPath = issue.path.join(".");
        const fullPath = fieldPath ? `${prefix}.${fieldPath}` : prefix;
        errors[fullPath] = issue.message;
      }
    }
  };

  const validateBorrowerFields = (
    borrower: BorrowerData | undefined,
    prefix: "primaryBorrower" | "coBorrower",
  ) => {
    if (!borrower) return;

    collectErrors(
      personalInfoSubmitSchema,
      {
        firstName: borrower.firstName,
        lastName: borrower.lastName,
        middleName: borrower.middleName,
        suffix: borrower.suffix,
        email: borrower.email,
        phone: borrower.phone,
        citizenship: borrower.citizenship || undefined,
      },
      prefix,
    );

    if (!borrower.phone?.trim()) {
      errors[`${prefix}.phone`] = "Phone number is required";
    }

    collectErrors(
      piiSubmitSchema,
      {
        dateOfBirth: borrower.dateOfBirth,
        ssn: borrower.ssn,
      },
      prefix,
    );

    collectErrors(
      maritalInfoSubmitSchema,
      {
        maritalStatus: borrower.maritalStatus || undefined,
        dependentsAgesYears: borrower.dependentsAgesYears,
      },
      prefix,
    );

    borrower.addresses.forEach((addr, i) => {
      const isMailing = addr.type === DomainAddressType.AddressMailing;
      const isCurrent = addr.type === DomainAddressType.AddressCurrent;

      if (isMailing) {
        collectErrors(
          mailingAddressSubmitSchema,
          {
            street: addr.street,
            city: addr.city,
            county: addr.county,
            state: addr.state,
            zipCode: addr.zipCode,
          },
          `${prefix}.addresses.${i}`,
        );
      } else if (isCurrent) {
        collectErrors(
          addressSubmitSchema,
          {
            street: addr.street,
            city: addr.city,
            county: addr.county,
            state: addr.state,
            zipCode: addr.zipCode,
            residedFromMonth: addr.residedFromMonth,
            residedFromYear: addr.residedFromYear,
            housingType: addr.housingType,
            monthlyPayment: addr.monthlyPayment,
            housingTypeOther: addr.housingTypeOther,
          },
          `${prefix}.addresses.${i}`,
        );
      } else {
        // PREVIOUS address — only validate basic fields + dates
        collectErrors(
          previousAddressSubmitSchema,
          {
            street: addr.street,
            city: addr.city,
            county: addr.county,
            state: addr.state,
            zipCode: addr.zipCode,
            residedFromMonth: addr.residedFromMonth,
            residedFromYear: addr.residedFromYear,
          },
          `${prefix}.addresses.${i}`,
        );
      }
    });

    borrower.employmentHistory.forEach((emp, i) => {
      collectErrors(
        employmentSubmitSchema,
        {
          employerName: emp.employerName,
          jobTitle: emp.jobTitle,
          startMonth: emp.startMonth,
          startYear: emp.startYear,
          isCurrent: emp.isCurrent,
          endMonth: emp.endMonth,
          endYear: emp.endYear,
          incomes: [{ amount: 1 }],
        },
        `${prefix}.employmentHistory.${i}`,
      );

      const baseIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeBase,
      );
      if (!baseIncome?.frequency) {
        errors[`${prefix}.employmentHistory.${i}.incomes.base.frequency`] =
          "Compensation type is required";
      }
      if (!baseIncome?.amount || baseIncome.amount < 1) {
        errors[`${prefix}.employmentHistory.${i}.incomes.base.amount`] =
          "Amount must be at least 1";
      }
      if (
        baseIncome?.frequency === DomainFrequencyType.FrequencyHourly &&
        (!baseIncome.weeklyHours || baseIncome.weeklyHours < 1)
      ) {
        errors[`${prefix}.employmentHistory.${i}.incomes.base.weeklyHours`] =
          "Hours per week is required";
      }

      const bonusIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeBonus,
      );
      if (bonusIncome) {
        if (!bonusIncome.frequency) {
          errors[`${prefix}.employmentHistory.${i}.incomes.bonus.frequency`] =
            "Frequency is required";
        }
        if (!bonusIncome.amount || bonusIncome.amount < 1) {
          errors[`${prefix}.employmentHistory.${i}.incomes.bonus.amount`] =
            "Amount must be at least 1";
        }
      }

      const commissionIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeCommission,
      );
      if (commissionIncome) {
        if (!commissionIncome.frequency) {
          errors[
            `${prefix}.employmentHistory.${i}.incomes.commission.frequency`
          ] = "Frequency is required";
        }
        if (!commissionIncome.amount || commissionIncome.amount < 1) {
          errors[`${prefix}.employmentHistory.${i}.incomes.commission.amount`] =
            "Amount must be at least 1";
        }
      }

      const overtimeIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeOvertime,
      );
      if (overtimeIncome) {
        if (!overtimeIncome.amount || overtimeIncome.amount < 1) {
          errors[`${prefix}.employmentHistory.${i}.incomes.overtime.amount`] =
            "Amount must be at least 1";
        }
        if (
          overtimeIncome.frequency === DomainFrequencyType.FrequencyHourly &&
          (!overtimeIncome.overtimeHours || overtimeIncome.overtimeHours < 1)
        ) {
          errors[
            `${prefix}.employmentHistory.${i}.incomes.overtime.overtimeHours`
          ] = "Overtime hours per week is required";
        }
      }
    });

    Object.assign(errors, validateEmploymentForSubmit(borrower, prefix));

    borrower.financialAccounts.forEach((acct, i) => {
      collectErrors(
        financialAccountSubmitSchema,
        {
          institutionName: acct.institutionName,
          accountType: acct.accountType || undefined,
          balance: acct.balance ?? 0,
        },
        `${prefix}.financialAccounts.${i}`,
      );
    });

    borrower.additionalIncome.forEach((inc, i) => {
      collectErrors(
        additionalIncomeSubmitSchema,
        {
          type: inc.type || undefined,
          monthlyAmount: inc.monthlyAmount ?? 0,
        },
        `${prefix}.additionalIncome.${i}`,
      );

      const isChildSupportOrSSI =
        inc.type === DomainIncomeType.IncomeChildSupport ||
        inc.type === DomainIncomeType.IncomeSSIForDependents;

      if (isChildSupportOrSSI) {
        if (!inc.childrenAges || inc.childrenAges.length === 0) {
          errors[`${prefix}.additionalIncome.${i}.childrenAges`] =
            "Children ages are required";
        }
      } else if (inc.type) {
        if (!inc.startMonth)
          errors[`${prefix}.additionalIncome.${i}.startMonth`] =
            "Start month is required";
        if (!inc.startYear)
          errors[`${prefix}.additionalIncome.${i}.startYear`] =
            "Start year is required";
      }
    });

    borrower.expenses.forEach((exp, i) => {
      collectErrors(
        expenseSubmitSchema,
        {
          type: exp.type || undefined,
          monthlyAmount: exp.monthlyAmount ?? 0,
        },
        `${prefix}.expenses.${i}`,
      );

      if (exp.type === DomainExpenseType.ExpenseChildSupport) {
        if (!exp.startMonth)
          errors[`${prefix}.expenses.${i}.startMonth`] =
            "Start month is required";
        if (!exp.startYear)
          errors[`${prefix}.expenses.${i}.startYear`] =
            "Start year is required";
        if (!exp.isOngoing) {
          if (!exp.endMonth)
            errors[`${prefix}.expenses.${i}.endMonth`] =
              "End month is required";
          if (!exp.endYear)
            errors[`${prefix}.expenses.${i}.endYear`] = "End year is required";
        }
        if (!exp.childrenAges || exp.childrenAges.length === 0) {
          errors[`${prefix}.expenses.${i}.childrenAges`] =
            "Children ages are required";
        }
      } else if (exp.type) {
        if (!exp.isOngoing) {
          if (!exp.endMonth)
            errors[`${prefix}.expenses.${i}.endMonth`] =
              "End month is required";
          if (!exp.endYear)
            errors[`${prefix}.expenses.${i}.endYear`] = "End year is required";
        }
      }
    });

    if (!borrower.biologicalSex) {
      errors[`${prefix}.biologicalSex`] = "Biological sex is required";
    }
    if (!borrower.ethnicities?.length) {
      errors[`${prefix}.ethnicities`] = "Ethnicity is required";
    }
    if (!borrower.races?.length) {
      errors[`${prefix}.races`] = "Race is required";
    }

    const hasCurrentAddress = borrower.addresses.some(
      (addr) => addr.type === DomainAddressType.AddressCurrent,
    );
    if (!hasCurrentAddress) {
      errors[`${prefix}.addresses._current`] =
        REQUIRED_EMPTY_LIST_MESSAGES.currentAddress;
    }

    if (borrower.employmentHistory.length === 0 && !borrower.employmentStatus) {
      errors[`${prefix}.employmentStatus`] = "Employment status is required";
    }

    // "Employed" requires at least one employment record
    if (
      borrower.employmentStatus ===
        DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed &&
      borrower.employmentHistory.length === 0
    ) {
      errors[`${prefix}.employmentStatus`] =
        REQUIRED_EMPTY_LIST_MESSAGES.employmentHistory;
    }

    borrower.employmentHistory.forEach((emp, i) => {
      const empAddr = emp.employerAddress;
      if (empAddr) {
        if (!empAddr.street?.trim())
          errors[`${prefix}.employmentHistory.${i}.employerAddress.street`] =
            "Employer address is required";
        if (!empAddr.city?.trim())
          errors[`${prefix}.employmentHistory.${i}.employerAddress.city`] =
            "City is required";
        if (!empAddr.state?.trim())
          errors[`${prefix}.employmentHistory.${i}.employerAddress.state`] =
            "State is required";
        if (!empAddr.zipCode?.trim())
          errors[`${prefix}.employmentHistory.${i}.employerAddress.zipCode`] =
            "Zip code is required";
      } else {
        errors[`${prefix}.employmentHistory.${i}.employerAddress.street`] =
          "Employer address is required";
      }
    });
  };

  validateBorrowerFields(data.primaryBorrower, "primaryBorrower");
  if (data.hasCoBorrower && data.coBorrower) {
    validateBorrowerFields(data.coBorrower, "coBorrower");
  }

  collectErrors(
    homeSubmitSchema,
    {
      manufacturer: data.home.manufacturer,
      modelName: data.home.modelName,
      modelYear: data.home.modelYear ?? undefined,
      homeType: data.home.homeType,
      primaryUse: data.home.primaryUse,
    },
    "home",
  );

  collectErrors(
    pricingSubmitSchema,
    {
      homePrice: data.pricing.homePrice ?? undefined,
      downPaymentAmount: data.pricing.downPaymentAmount ?? undefined,
      downPaymentSource: data.pricing.downPaymentSource || undefined,
      downPaymentSourceOther: data.pricing.downPaymentSourceOther || undefined,
    },
    "pricing",
  );

  if (data.land.propertyAddress) {
    validateAddressFields(
      data.land.propertyAddress,
      `land.propertyAddress`,
      ["zipCode"],
      (key, error) => Object.assign(errors, { [key]: error }),
      (key) => errors[key] !== undefined,
      () => {},
      true,
    );
  }

  Object.assign(errors, validateLandForSubmit(data.land));

  if (!data.lenders.some((l) => l.enabled)) {
    errors["lenders.enabled"] = "At least one lender must be enabled";
  }

  return errors;
}

const snakeToCamel = (s: string) =>
  s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

const DEFAULT_REQUIRED_FIELD_MESSAGE = "This field is required.";

/** User-facing messages when a required list section has no items. */
export const REQUIRED_EMPTY_LIST_MESSAGES = {
  employmentHistory: "Please add at least one job to employment history",
  currentAddress: "Please add a current address",
} as const;

/** Backend empty-list failures for optional sections — not surfaced in the UI. */
const OPTIONAL_EMPTY_LIST_BACKEND_FIELDS = new Set([
  "no_incomes_provided",
  "no_assets_provided",
  "no_expenses_provided",
]);

/** Backend validation_failure field names that differ from frontend BorrowerData keys. */
const BACKEND_TO_FRONTEND_FIELD_ALIASES: Record<string, string> = {
  phone_number: "phone",
  dependents_ages: "dependentsAgesYears",
  citizenship_type: "citizenship",
  ethnicity: "ethnicities",
  race: "races",
};

/** Backend fields that indicate an empty required list rather than a scalar field. */
const EMPTY_LIST_BACKEND_FIELDS: Record<
  string,
  { frontendKey: string; message: string }
> = {
  employment_records: {
    frontendKey: "employmentStatus",
    message: REQUIRED_EMPTY_LIST_MESSAGES.employmentHistory,
  },
  no_current_address: {
    frontendKey: "addresses._current",
    message: REQUIRED_EMPTY_LIST_MESSAGES.currentAddress,
  },
};

const BACKEND_SCALAR_FIELD_MESSAGES: Record<string, string> = {
  employment_status: "Employment status is required",
  employment_base_weekly_hours: "Hours per week is required",
  employment_bonus_amount: "Amount must be at least 1",
  employment_bonus_frequency: "Frequency is required",
  employment_commission_amount: "Amount must be at least 1",
  employment_commission_frequency: "Frequency is required",
  employment_overtime_amount: "Amount must be at least 1",
  employment_overtime_weekly_hours: "Overtime hours per week is required",
  dependents_ages:
    "Dependents must be confirmed. Leave blank if you have no dependents.",
};

/** Backend property_validation_failures field names → frontend form paths. */
const PROPERTY_FIELD_ALIASES: Record<string, string> = {
  manufacturer_type: "home.manufacturer",
  model_name: "home.modelName",
  model_year: "home.modelYear",
  section_type: "home.homeType",
  price: "pricing.homePrice",
  land_situation_type: "land.landSituation",
  use_as_down_payment: "land.useAsDownPayment",
  land_value: "land.estimatedLandValue",
  purchase_date: "land.purchaseDateMonth",
  acreage: "land.acreage",
  has_frontage_public_road: "land.hasFrontagePublicRoad",
  has_paved_road: "land.hasPavedRoad",
  is_refinance: "land.isRefinance",
  monthly_payment: "land.monthlyLandPayment",
  land_owner: "land.landlordName",
  purchase_price_payoff: "land.existingMortgageBalance",
  lease_type: "land.leaseType",
  hoa_fee_amount: "land.hoaFeeAmount",
};

/** Backend loan_overview_validation_failures field names → frontend form paths. */
const LOAN_OVERVIEW_FIELD_ALIASES: Record<string, string> = {
  down_payment_source_type: "pricing.downPaymentSource",
  down_payment_source_other: "pricing.downPaymentSourceOther",
};

const ADDRESS_FIELD_ALIASES: Record<string, string> = {
  street1: "street",
  city: "city",
  state: "state",
  zip_code: "zipCode",
  address_required: "street",
};

const EMPLOYMENT_RECORD_FIELD_ALIASES: Record<string, string> = {
  employment_employer_name: "employerName",
  employment_position_title: "jobTitle",
  employment_start_date: "startYear",
  employment_address: "employerAddress.street",
  employment_base_weekly_hours: "incomes.base.weeklyHours",
  employment_bonus_amount: "incomes.bonus.amount",
  employment_bonus_frequency: "incomes.bonus.frequency",
  employment_commission_amount: "incomes.commission.amount",
  employment_commission_frequency: "incomes.commission.frequency",
  employment_overtime_amount: "incomes.overtime.amount",
  employment_overtime_weekly_hours: "incomes.overtime.overtimeHours",
};

const EMPLOYMENT_ADDRESS_FIELD_ALIASES: Record<string, string> = {
  street1: "street",
  city: "city",
  state: "state",
  zip_code: "zipCode",
};

const INCOME_RECORD_FIELD_ALIASES: Record<string, string> = {
  amount: "monthlyAmount",
  income_type: "type",
  income_type_other: "typeOther",
  start_date: "startYear",
};

const ASSET_RECORD_FIELD_ALIASES: Record<string, string> = {
  balance_amount: "balance",
  asset_type: "accountType",
  institution_name: "institutionName",
};

const EXPENSE_RECORD_FIELD_ALIASES: Record<string, string> = {
  amount: "monthlyAmount",
  expense_type: "type",
  expense_type_other: "typeOther",
};

function mapIndexedRecordField(
  field: string,
  pattern: RegExp,
  fieldAliases: Record<string, string>,
  listKey: string,
): string | null {
  const match = field.match(pattern);
  if (!match) return null;
  const [, index, subfield] = match;
  const mapped = fieldAliases[subfield];
  if (!mapped) return null;
  return `${listKey}.${index}.${mapped}`;
}

/**
 * Map a backend validation_failures field (within a section) to the frontend
 * borrower-relative field path used by forms and TOC error highlighting.
 */
function mapBackendFailureToFrontendKey(
  section: string,
  field: string,
): string | null {
  if (OPTIONAL_EMPTY_LIST_BACKEND_FIELDS.has(field)) return null;

  const emptyListField = EMPTY_LIST_BACKEND_FIELDS[field];
  if (emptyListField) return emptyListField.frontendKey;

  if (field in BACKEND_TO_FRONTEND_FIELD_ALIASES) {
    return BACKEND_TO_FRONTEND_FIELD_ALIASES[field];
  }

  switch (section) {
    case "address": {
      const mapped = ADDRESS_FIELD_ALIASES[field];
      if (mapped) return `addresses._current.${mapped}`;
      break;
    }
    case "employment": {
      if (field === "employment_status") return "employmentStatus";
      const mapped = EMPLOYMENT_RECORD_FIELD_ALIASES[field];
      if (mapped) return `employmentHistory.0.${mapped}`;
      const employmentAddressMatch = field.match(/^employment_address_(.+)$/);
      if (employmentAddressMatch) {
        const addrField =
          EMPLOYMENT_ADDRESS_FIELD_ALIASES[employmentAddressMatch[1]];
        if (addrField)
          return `employmentHistory.0.employerAddress.${addrField}`;
      }
      break;
    }
    case "income":
      return mapIndexedRecordField(
        field,
        /^income_(\d+)_(.+)$/,
        INCOME_RECORD_FIELD_ALIASES,
        "additionalIncome",
      );
    case "financial_accounts":
      return mapIndexedRecordField(
        field,
        /^asset_(\d+)_(.+)$/,
        ASSET_RECORD_FIELD_ALIASES,
        "financialAccounts",
      );
    case "expenses":
      return mapIndexedRecordField(
        field,
        /^expense_(\d+)_(.+)$/,
        EXPENSE_RECORD_FIELD_ALIASES,
        "expenses",
      );
    case "personal_info":
    case "consent":
    case "demographics":
      return snakeToCamel(field);
    default:
      break;
  }

  return snakeToCamel(field);
}

function backendFieldErrorMessage(field: string): string {
  const emptyListField = EMPTY_LIST_BACKEND_FIELDS[field];
  if (emptyListField) {
    return emptyListField.message;
  }
  return BACKEND_SCALAR_FIELD_MESSAGES[field] ?? DEFAULT_REQUIRED_FIELD_MESSAGE;
}

/**
 * Translate the backend's per-section validation failures (snake_case field
 * names grouped by section) into frontend field-error entries keyed by the
 * borrower-prefixed camelCase path (e.g. consent -> date_of_birth becomes
 * "primaryBorrower.dateOfBirth"). Lets "Check required fields" surface exactly
 * what the server still considers missing, closing the gap where the frontend
 * believes a field is filled but the backend never received it.
 */
export function mapBackendFailuresToFieldErrors(
  failures: Record<string, string[]> | undefined,
  prefix: "primaryBorrower" | "coBorrower",
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!failures) return out;

  const allFields = new Set(Object.values(failures).flat());

  for (const [section, fields] of Object.entries(failures)) {
    for (const field of fields) {
      if (
        field === "employment_records" &&
        allFields.has("employment_status")
      ) {
        continue;
      }
      const frontendKey = mapBackendFailureToFrontendKey(section, field);
      if (!frontendKey) continue;
      out[`${prefix}.${frontendKey}`] = backendFieldErrorMessage(field);
    }
  }
  return out;
}

function mapTopLevelBackendFailuresToFieldErrors(
  fields: string[] | undefined,
  aliases: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!fields) return out;

  for (const field of fields) {
    const frontendKey = aliases[field];
    if (!frontendKey) continue;
    out[frontendKey] = backendFieldErrorMessage(field);
    if (field === "monthly_payment") {
      out["land.monthlyLeasePayment"] = backendFieldErrorMessage(field);
    }
  }
  return out;
}

/**
 * Map all backend validation failure sources from a borrower progress snapshot
 * into frontend field-error entries (borrower-prefixed paths plus shared
 * home/land/pricing paths for the primary borrower).
 */
export function mapBorrowerProgressToFieldErrors(
  progress:
    | {
        validation_failures?: Record<string, string[]>;
        property_validation_failures?: string[];
        loan_overview_validation_failures?: string[];
      }
    | undefined,
  prefix: "primaryBorrower" | "coBorrower",
): Record<string, string> {
  if (!progress) return {};

  const out = {
    ...mapBackendFailuresToFieldErrors(progress.validation_failures, prefix),
  };

  if (prefix === "primaryBorrower") {
    Object.assign(
      out,
      mapTopLevelBackendFailuresToFieldErrors(
        progress.property_validation_failures,
        PROPERTY_FIELD_ALIASES,
      ),
      mapTopLevelBackendFailuresToFieldErrors(
        progress.loan_overview_validation_failures,
        LOAN_OVERVIEW_FIELD_ALIASES,
      ),
    );
  }

  return out;
}

/**
 * Clear employment income field errors that are now satisfied by the current
 * income rows. Also clears errors for supplemental income types that were
 * removed from the form.
 */
export function clearValidEmploymentIncomeFieldErrors(
  incomes: EmploymentIncome[],
  errorKeyPrefix: string,
  fieldErrors: Record<string, string>,
  clearFieldError: (field: string) => void,
): void {
  const clearIfPresent = (suffix: string, isValid: boolean) => {
    const key = `${errorKeyPrefix}.${suffix}`;
    if (isValid && fieldErrors[key]) {
      clearFieldError(key);
    }
  };

  const baseIncome = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeBase,
  );
  clearIfPresent("incomes.base.frequency", !!baseIncome?.frequency);
  clearIfPresent(
    "incomes.base.amount",
    !!baseIncome?.amount && baseIncome.amount >= 1,
  );
  clearIfPresent(
    "incomes.base.weeklyHours",
    baseIncome?.frequency === DomainFrequencyType.FrequencyHourly &&
      !!baseIncome.weeklyHours &&
      baseIncome.weeklyHours >= 1,
  );

  const bonusIncome = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeBonus,
  );
  if (bonusIncome) {
    clearIfPresent("incomes.bonus.frequency", !!bonusIncome.frequency);
    clearIfPresent(
      "incomes.bonus.amount",
      !!bonusIncome.amount && bonusIncome.amount >= 1,
    );
  } else {
    clearIfPresent("incomes.bonus.frequency", true);
    clearIfPresent("incomes.bonus.amount", true);
  }

  const commissionIncome = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeCommission,
  );
  if (commissionIncome) {
    clearIfPresent(
      "incomes.commission.frequency",
      !!commissionIncome.frequency,
    );
    clearIfPresent(
      "incomes.commission.amount",
      !!commissionIncome.amount && commissionIncome.amount >= 1,
    );
  } else {
    clearIfPresent("incomes.commission.frequency", true);
    clearIfPresent("incomes.commission.amount", true);
  }

  const overtimeIncome = incomes.find(
    (inc) => inc.incomeType === DomainIncomeType.IncomeOvertime,
  );
  if (overtimeIncome) {
    clearIfPresent(
      "incomes.overtime.amount",
      !!overtimeIncome.amount && overtimeIncome.amount >= 1,
    );
    clearIfPresent(
      "incomes.overtime.overtimeHours",
      overtimeIncome.frequency === DomainFrequencyType.FrequencyHourly &&
        !!overtimeIncome.overtimeHours &&
        overtimeIncome.overtimeHours >= 1,
    );
  } else {
    clearIfPresent("incomes.overtime.amount", true);
    clearIfPresent("incomes.overtime.overtimeHours", true);
  }
}

/**
 * Merge frontend validation errors with backend-mapped errors.
 *
 * - `freshBackend: true` — used right after a progress API fetch (e.g. "Check
 *   required fields"); all backend failures are included even when the frontend
 *   believes a field is filled.
 * - Default (stale snapshot) — only keeps backend errors for keys the frontend
 *   still reports as invalid, so fixing a field clears highlights and TOC dots.
 */
export function mergeValidationErrors(
  frontendErrors: Record<string, string>,
  backendErrors: Record<string, string>,
  options?: { freshBackend?: boolean },
): Record<string, string> {
  if (options?.freshBackend) {
    return { ...frontendErrors, ...backendErrors };
  }

  if (Object.keys(backendErrors).length === 0) {
    return { ...frontendErrors };
  }

  // Backend-only snapshot (no frontend errors yet) — keep all backend failures
  // visible until the user starts editing.
  if (Object.keys(frontendErrors).length === 0) {
    return { ...backendErrors };
  }

  const merged = { ...frontendErrors };
  for (const [key, message] of Object.entries(backendErrors)) {
    if (key in frontendErrors) {
      merged[key] = message;
    }
  }
  return merged;
}

/** Preserve API uniqueness errors when replacing the validation error set. */
export function preserveApiFieldErrors(
  fieldErrors: Record<string, string>,
): Record<string, string> {
  const apiErrors: Record<string, string> = {};
  for (const [key, message] of Object.entries(fieldErrors)) {
    if (message.toLowerCase().includes("already in use")) {
      apiErrors[key] = message;
    }
  }
  return apiErrors;
}

/**
 * Derive per-borrower pass/fail flags from a validation error map. Shared
 * (home/land/pricing/lenders) errors fail BOTH borrowers.
 */
export function computeValidationFlags(errors: Record<string, string>): {
  primaryPassed: boolean;
  coPassed: boolean;
} {
  const errorEntries = Object.entries(errors);
  const hasSharedErrors = errorEntries.some(([key]) =>
    ["home.", "land.", "pricing.", "lenders."].some((p) => key.startsWith(p)),
  );
  const primaryHasErrors =
    hasSharedErrors ||
    errorEntries.some(([key]) => key.startsWith("primaryBorrower."));
  const coHasErrors =
    hasSharedErrors ||
    errorEntries.some(([key]) => key.startsWith("coBorrower."));
  return { primaryPassed: !primaryHasErrors, coPassed: !coHasErrors };
}

function hasStepValidationErrors(
  step: ApplicationStep,
  errors: Record<string, string>,
): boolean {
  return Object.keys(errors).some((key) => {
    switch (step) {
      case "primary-borrower":
        return key.startsWith("primaryBorrower.");
      case "co-borrower":
        return key.startsWith("coBorrower.");
      case "property":
        return key.startsWith("home.") || key.startsWith("land.");
      case "pricing":
        return key.startsWith("pricing.");
      case "lenders":
        return key.startsWith("lenders.");
      default:
        return false;
    }
  });
}

function capProgressForValidationErrors(
  progress: number,
  step: ApplicationStep,
  errors: Record<string, string>,
): number {
  return hasStepValidationErrors(step, errors)
    ? Math.min(progress, 99)
    : progress;
}

/** Count how many truthy, non-empty values are in the list. */
function countFilled(
  fields: (string | number | boolean | undefined | null)[],
): number {
  return fields.filter(
    (f) => f !== undefined && f !== null && f !== "" && f !== 0,
  ).length;
}

function allRecordsComplete<T>(records: T[], requiredFields: (keyof T)[]) {
  return (
    records.length === 0 ||
    records.every((r) =>
      requiredFields.every((f) => {
        const v = r[f];
        return v !== undefined && v !== null && v !== "" && v !== 0;
      }),
    )
  );
}

function computeBorrowerProgress(b: BorrowerData): number {
  let total = 15; // base total (removed financialAccounts as always-required)
  let passed = 0;

  passed += countFilled([
    b.firstName,
    b.lastName,
    b.email,
    b.phone,
    b.citizenship,
    b.dateOfBirth,
    b.ssn,
    b.maritalStatus,
  ]);

  if (b.biologicalSex) passed++;
  if (b.ethnicities?.length) passed++;
  if (b.races?.length) passed++;

  const hasCurrentAddress = b.addresses.some(
    (addr) =>
      addr.type === DomainAddressType.AddressCurrent &&
      addr.street &&
      addr.city &&
      addr.state &&
      addr.zipCode,
  );
  if (hasCurrentAddress) passed++;

  if (b.hasSufficientAddressHistory) passed++;

  if (b.employmentStatus || b.employmentHistory.length > 0) passed++;
  if (b.hasSufficientEmploymentHistory) passed++;

  const employmentRecordsComplete =
    b.employmentHistory.length === 0 ||
    b.employmentHistory.every((emp) => {
      const baseIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeBase,
      );
      const bonusIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeBonus,
      );
      const commissionIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeCommission,
      );
      const overtimeIncome = emp.incomes.find(
        (inc) => inc.incomeType === DomainIncomeType.IncomeOvertime,
      );
      const baseComplete = !!(
        baseIncome?.frequency &&
        baseIncome?.amount &&
        baseIncome.amount >= 1 &&
        (baseIncome.frequency !== DomainFrequencyType.FrequencyHourly ||
          (baseIncome.weeklyHours && baseIncome.weeklyHours >= 1))
      );
      const bonusComplete =
        !bonusIncome ||
        (!!bonusIncome.frequency &&
          !!bonusIncome.amount &&
          bonusIncome.amount >= 1);
      const commissionComplete =
        !commissionIncome ||
        (!!commissionIncome.frequency &&
          !!commissionIncome.amount &&
          commissionIncome.amount >= 1);
      const overtimeComplete =
        !overtimeIncome ||
        (!!overtimeIncome.amount &&
          overtimeIncome.amount >= 1 &&
          (overtimeIncome.frequency !== DomainFrequencyType.FrequencyHourly ||
            (overtimeIncome.overtimeHours != null &&
              overtimeIncome.overtimeHours >= 1)));
      return (
        baseComplete && bonusComplete && commissionComplete && overtimeComplete
      );
    });
  if (b.employmentHistory.length > 0) {
    total++;
    if (employmentRecordsComplete) passed++;
  }

  const financialAccountsComplete = allRecordsComplete(b.financialAccounts, [
    "accountType",
    "institutionName",
    "balance",
  ]);
  if (b.financialAccounts.length > 0) {
    total++;
    if (financialAccountsComplete) passed++;
  }

  const additionalIncomeComplete = allRecordsComplete(b.additionalIncome, [
    "type",
    "monthlyAmount",
  ]);
  if (b.additionalIncome.length > 0) {
    total++;
    if (additionalIncomeComplete) passed++;
  }

  const expensesComplete = allRecordsComplete(b.expenses, [
    "type",
    "monthlyAmount",
  ]);
  if (b.expenses.length > 0) {
    total++;
    if (expensesComplete) passed++;
  }

  return Math.round((passed / total) * 100);
}

export function computeApplicationStepProgress(
  data: ApplicationFormData,
  step: ApplicationStep,
  validationErrors: Record<string, string> = validateApplication(data),
): number {
  switch (step) {
    case "summary":
      return 0;
    case "primary-borrower":
      return capProgressForValidationErrors(
        computeBorrowerProgress(data.primaryBorrower),
        step,
        validationErrors,
      );
    case "co-borrower": {
      if (!data.hasCoBorrower || !data.coBorrower) return 0;
      return capProgressForValidationErrors(
        computeBorrowerProgress(data.coBorrower),
        step,
        validationErrors,
      );
    }
    case "property": {
      const checks = [
        data.home.homeType,
        data.home.primaryUse,
        data.home.manufacturer,
        data.home.modelName,
        data.home.modelYear && data.home.modelYear > 0
          ? data.home.modelYear
          : undefined,
        data.land.landSituation,
        data.land.propertyAddress?.zipCode,
      ];
      return capProgressForValidationErrors(
        Math.round((countFilled(checks) / checks.length) * 100),
        step,
        validationErrors,
      );
    }
    case "pricing": {
      let passed = 0;
      let total = 2;
      if (data.pricing.homePrice && data.pricing.homePrice > 0) passed++;
      if (data.pricing.loanAmount && data.pricing.loanAmount > 0) passed++;
      if (
        data.pricing.downPaymentAmount &&
        data.pricing.downPaymentAmount > 0
      ) {
        total++;
        if (data.pricing.downPaymentSource) passed++;
      }
      return capProgressForValidationErrors(
        Math.round((passed / total) * 100),
        step,
        validationErrors,
      );
    }
    case "lenders":
      return capProgressForValidationErrors(
        data.lenders.some((l) => l.enabled) ? 100 : 0,
        step,
        validationErrors,
      );
    case "signature": {
      const sig = data.signature;
      let passed = 0;
      const total = data.hasCoBorrower ? 2 : 1;
      if (sig.borrowerStatus === "signed") passed++;
      if (data.hasCoBorrower && sig.coBorrowerStatus === "signed") passed++;
      return Math.round((passed / total) * 100);
    }
    default:
      return 0;
  }
}

export function computeApplicationOverallProgress(
  data: ApplicationFormData,
  validationErrors: Record<string, string> = validateApplication(data),
): number {
  const steps = (
    [
      "primary-borrower",
      "co-borrower",
      "property",
      "pricing",
      "lenders",
    ] satisfies ApplicationStep[]
  ).filter((s) => s !== "co-borrower" || data.hasCoBorrower);
  const progress = steps.map((step) =>
    computeApplicationStepProgress(data, step, validationErrors),
  );
  return Math.round(progress.reduce((a, b) => a + b, 0) / progress.length);
}

const SECTION_LABELS: Record<string, string> = {
  "primary-borrower": "Primary Borrower",
  "co-borrower": "Co-Borrower",
  property: "Land & Home",
  pricing: "Pricing",
  lenders: "Lenders",
};

/**
 * Map a validation error map to the sorted list of top-level section labels
 * that contain errors (for the "needs to be completed" toast).
 */
export function summarizeErrorSections(
  errors: Record<string, string>,
): string[] {
  const sections = new Set<string>();
  for (const fieldPath of Object.keys(errors)) {
    if (fieldPath.startsWith("primaryBorrower."))
      sections.add("primary-borrower");
    else if (fieldPath.startsWith("coBorrower.")) sections.add("co-borrower");
    else if (fieldPath.startsWith("home.") || fieldPath.startsWith("land."))
      sections.add("property");
    else if (fieldPath.startsWith("pricing.")) sections.add("pricing");
    else if (fieldPath.startsWith("lenders.")) sections.add("lenders");
  }
  return [...sections].map((id) => SECTION_LABELS[id] || id).sort();
}
